import type { ChatAttachment } from '../types/chat.types'
import type { IHttpClient } from './httpClient'
import { getAttachmentKind } from '../utils/attachment'

interface PresignedUrlResponse {
  presignedUrl: string
  uniqueFilename: string
  userFilename: string
  bucket: string
  expiresIn: number
}

export interface IAttachmentService {
  stageFile(file: File, onProgress?: (progress: number) => void): Promise<ChatAttachment>
}

export class AttachmentService implements IAttachmentService {
  constructor(private httpClient: IHttpClient) {}

  async stageFile(file: File, onProgress?: (progress: number) => void): Promise<ChatAttachment> {
    if (!file) {
      throw new Error('No file provided')
    }

    const fileType = file.type || 'application/octet-stream'
    const presignPayload = {
      fileType,
      filename: file.name,
    }

    const response = await this.httpClient.post<PresignedUrlResponse>(
      '/presigned-url',
      presignPayload
    )

    if (!response.presignedUrl || !response.uniqueFilename) {
      throw new Error('Unable to prepare attachment upload')
    }

    await this.uploadToS3(response.presignedUrl, file, fileType, onProgress)

    return {
      uniqueFilename: response.uniqueFilename,
      userFilename: response.userFilename || file.name,
      contentType: fileType,
      size: file.size,
      kind: getAttachmentKind(fileType, file.name),
    }
  }

  private async uploadToS3(
    url: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (!event.lengthComputable || !onProgress) return
        onProgress((event.loaded / event.total) * 100)
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
          return
        }
        reject(new Error(`Failed to upload file to storage (${xhr.status})`))
      }

      xhr.onerror = () => {
        reject(new Error('Network error during file upload'))
      }

      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', contentType)
      xhr.send(file)
    })
  }
}

